import Button from 'Shared/components/Button'
import Select from 'react-select'
import Loading from 'Shared/components/Loading'
import './AccessControl.scoped.scss'
import useAct from '../hooks/useAct'
import { selectRootOrgRule, selectNonRootOrgRules } from '../selectors'
import OrganisationPickerButton from 'Shared/components/OrganisationPicker/OrganisationPickerButton'

const ORGANISATION_KINDS = window.ENV.ORGANISATION_KINDS

const ACCESS_KIND_OPTIONS = [
  { value: 'view', label: 'To view' },
  { value: 'volunteer', label: 'To volunteer' },
]

function SubOrgGrant({ label, externalType, accessKind, enabled, onChange }) {
  const customStyles = {
    control: (provided) => ({
      ...provided,
      minWidth: '200px',
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  }

  const valueOption = ACCESS_KIND_OPTIONS.find((option) => option.value === accessKind)

  const handleEnabledChange = (e) => onChange(e.target.checked, accessKind)
  const handleKindChange = (option) => {
    onChange(enabled, option.value)
  }

  return (
    <div className="box sub-organisation-kind flex-center">
      <p className="grow no-margin-bottom">
        <input type="checkbox" checked={enabled} onChange={handleEnabledChange} />
        <strong>{label}</strong>
      </p>

      <Select
        options={ACCESS_KIND_OPTIONS}
        onChange={handleKindChange}
        value={valueOption}
        styles={customStyles}
        menuPortalTarget={document.body}
        classNamePrefix="access-kind-select"
      />
    </div>
  )
}

export default function AccessControlContent({ entityType, entityId, entityName, inaccessible }) {
  const act = useAct()

  const [rootOrgOnly, setRootOrgOnly] = useState(true)
  const [specificOrgsOnly, setSpecificOrgsOnly] = useState(null)
  const [rootOrgRule, setRootOrgRule] = useState(null)
  const { rules, dirty, loading, error, organisationNames } = useSel((s) => s.access)

  useEffect(() => {
    act.access.load(entityType, entityId).then((rules) => {
      setSpecificOrgsOnly(false);
      if (rules?.organisations.length === 1 && selectRootOrgRule(rules) === 'none') {
        setRootOrgOnly(true);
      }
      if (rules?.organisations.length === 1 && selectRootOrgRule(rules) !== 'none') {
        setRootOrgOnly(false);
      }
      if (rules?.organisations.length > 1) {
        setRootOrgOnly(false);
      }
      if (selectNonRootOrgRules(rules).length > 0) {
        setSpecificOrgsOnly(true);
      }
      act.access.fetchOrganisationNames(rules?.organisations.map(orgRule => orgRule.id) || [])
      setRootOrgRule(selectRootOrgRule(rules))
    })
  }, [])

  useEffect(() => {
    // Hide share button if there is an error (when the inaccessible callback is provided)
    if (error && inaccessible) {
      inaccessible()
    }
  }, [error])

  useEffect(() => {
    if (rules && rules.organisations.length > 0) {
      setRootOrgRule(selectRootOrgRule(rules))
      let preLoaded = Object.keys(organisationNames) // already loaded
      const idsToLoad = rules.organisations.map(orgRule => orgRule.id).filter(id => !preLoaded.includes(id))
      act.access.fetchOrganisationNames(idsToLoad)
    }
  }, [rules?.organisations])

  useEffect(() => {
    // If checking with people in my organisation only, ensure root grant type is 'none'
    if (rootOrgOnly && rootOrgRule) {
      if (rootOrgRule.grantToSuborganisations === 'all') {
        act.access.updateRootOrgRule('none')
      }
    }
    // If there are multiple orgs, set it to specific orgs not structure
    if (specificOrgsOnly === null || rootOrgOnly === true) {
      setSpecificOrgsOnly(rules && rules.organisations.length > 1)
    }
  }, [rootOrgOnly])

  if (loading) {
    return <Loading />
  }

  if (error) {
    return <p>{error}</p>
  }

  if (!loading && !rootOrgRule) {
    return <p>Failed to load your organisation</p>
  }

  const persist = () => {
    act.access.persist(entityType, entityId)
  }

  const handleSubOrgRuleChange = (externalType, enabled, accessKind) => {
    act.access.updateSubOrgRule(externalType, enabled, accessKind)
  }

  const toggleRootOrgShareAll = () => {
    if (rootOrgRule.grantToSuborganisations === 'all') {
      act.access.updateRootOrgRule('none')
    } else {
      act.access.updateRootOrgRule('all')
    }
  }

  const setRootOrgAndNoSubOrgs = () => {
    act.access.removeSubOrgRules()
    setRootOrgOnly(true)
  }
  const setRootOrgAndSubOrgs = () => {
    act.access.updateRootOrgRule('none')
    act.access.removeSubOrgRules()
    setRootOrgOnly(false)
    setSpecificOrgsOnly(false)
  }
  const setSpecificOrgs = () => {
    act.access.removeSubOrgRules()
    act.access.updateRootOrgRule('none')
    setRootOrgOnly(false)
    setSpecificOrgsOnly(true)
  }

  return (
    <div className="container">
      <div className={rootOrgOnly ? 'box active' : 'box'}>
        <label className="no-margin-bottom">
          <input
            type="checkbox"
            checked={rootOrgOnly}
            name="rootOrgOnly"
            onClick={setRootOrgAndNoSubOrgs}
          />
          <span>
            With people within my organisation{' '}
            <strong>{window.ENV.ORGANISATION_NAME}</strong>
          </span>
        </label>
        <p className="no-margin-bottom dark-grey">
          Only allow users within your organisation to manage and take part in this
          campaign.
        </p>
      </div>

      <div className={(!rootOrgOnly && !specificOrgsOnly) ? 'box active' : 'box'}>
        <label className="no-margin-bottom" checked={!rootOrgOnly && !specificOrgsOnly}>
          <input
            type="checkbox"
            checked={!rootOrgOnly && !specificOrgsOnly}
            name="rootOrgAndSubOrgs"
            onClick={setRootOrgAndSubOrgs}
          />
          <span>
            With people in my organisation <strong>{window.ENV.ORGANISATION_NAME}</strong>
            , and organisations beneath
          </span>
        </label>
        <p className="no-margin-bottom dark-grey">
          Select organisation levels beneath yours which can manage and take part in this
          campaign.
        </p>

        {!rootOrgOnly && !specificOrgsOnly && (
          <div className="margin-top indented">
            <label className="margin-bottom">
              <input
                type="checkbox"
                checked={rootOrgRule.grantToSuborganisations === 'all'}
                name="shareWithAllSubOrgs"
                onClick={toggleRootOrgShareAll}
              />
              <span>
                Share with <strong>all</strong> people in{' '}
                <strong>{window.ENV.ORGANISATION_NAME}</strong> and <strong>all</strong>{' '}
                organisations beneath
              </span>
            </label>

            {rootOrgRule.grantToSuborganisations === 'none' && (
              <>
                <div className="margin-top margin-bottom">
                  <strong>Select types of organisations</strong> to share with
                </div>

                {ORGANISATION_KINDS.map((kind) => {
                  // The access record matching this sub-org kind
                  const rule = rules.organisations.find(
                    (org) =>
                      org.id === window.ENV.ORGANISATION_ID &&
                      org.grantToSuborganisations === kind.external_type
                  )
                  return (
                    <SubOrgGrant
                      key={kind.external_type}
                      label={kind.label}
                      externalType={kind.external_type}
                      enabled={!!rule}
                      accessKind={rule ? rule.accessKind : 'view'}
                      onChange={(enabled, accessKind) =>
                        handleSubOrgRuleChange(kind.external_type, enabled, accessKind)
                      }
                    />
                  )
                })}
              </>
            )}
          </div>
        )}
      </div>

      <div className={(!rootOrgOnly && specificOrgsOnly) ? 'box active' : 'box'}>
        <label className="no-margin-bottom">
          <input
            type="checkbox"
            checked={!rootOrgOnly && specificOrgsOnly}
            name="rootOrgAndSpecificOrgs"
            onClick={setSpecificOrgs}
          />
          <span>
            With people in my organisation <strong>{window.ENV.ORGANISATION_NAME}</strong>
            , and specific organisations beneath
          </span>
        </label>
        <p className="no-margin-bottom dark-grey">
          Select specific organisations beneath yours which can manage and take part in this
          campaign.
        </p>

        {!rootOrgOnly && specificOrgsOnly && (
          <div className="margin-top">
            <div>
              {selectNonRootOrgRules(rules).map(rule => <>
                <SubOrgGrant
                  key={rule.id}
                  label={organisationNames[rule.id]}
                  externalType={''}
                  enabled={true}
                  accessKind={rule.accessKind}
                  onChange={(enabled, accessKind) => {
                    if (!enabled) {
                      act.access.removeSpecificOrgRule(rule.id)
                    } else {
                      act.access.upsertSpecificOrgRule(rule.id, accessKind)
                    }
                  }}
                />
              </>)}
            </div>
            <div className='margin-top'>
              <OrganisationPickerButton title="Add Organisation" onPick={(a) => act.access.addSpecificOrgRule(a)} organisationTypesClickable={true} />
            </div>
          </div>
        )}
      </div>

      <div className="grow" />

      <div className="footer">
        {dirty && (
          <Button primary dark onClick={persist}>
            Update
          </Button>
        )}
        {!dirty && (
          <Button primary green disabled>
            Saved
          </Button>
        )}
      </div>
    </div>
  )
}
